import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink } from "lucide-react";

// Fix default marker icon paths (Leaflet + CRA quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Recenter helper when coords change or the container remounts
function Recenter({ coords, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (coords) map.setView(coords, zoom);
    setTimeout(() => map.invalidateSize(), 100);
  }, [coords, zoom, map]);
  return null;
}

/**
 * PropertyMap
 * Shows a satellite aerial view of the property (real photo of the home from
 * above) by default, with a toggle to switch to the street map. Also exposes a
 * "Street View" button that opens Google Street View for the address in a new
 * tab (no API key required — uses google.com/maps public URL).
 *
 * Geocoding via free Nominatim (OSM) — no API key required.
 */
export default function PropertyMap({
  address,
  height = 240,
  initialView = "satellite", // 'satellite' | 'map'
  hideToggle = false,        // hide the internal toggle (gallery drives view externally)
  hideStreetViewLink = false,
  zoomOverride = null,       // force a specific zoom level (else 19 for sat, 17 for map)
}) {
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ok | not_found
  const [view, setView] = useState(initialView); // 'satellite' | 'map'

  useEffect(() => {
    if (!address) return;
    setState("loading");
    setCoords(null);
    const url =
      "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
      encodeURIComponent(address);
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((rows) => {
        if (!rows || !rows.length) {
          setState("not_found");
          return;
        }
        setCoords([parseFloat(rows[0].lat), parseFloat(rows[0].lon)]);
        setState("ok");
      })
      .catch(() => setState("not_found"));
  }, [address]);

  if (state === "loading") {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-muted-foreground bg-secondary/40"
        data-testid="map-loading"
      >
        Locating property…
      </div>
    );
  }
  if (state === "not_found" || !coords) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-muted-foreground bg-secondary/40"
        data-testid="map-not-found"
      >
        Property image unavailable
      </div>
    );
  }

  // Google Street View URL — works with just an address, no API key needed
  const streetViewUrl = `https://www.google.com/maps?q=&layer=c&cbll=${coords[0]},${coords[1]}`;
  const zoom = zoomOverride ?? (view === "satellite" ? 19 : 17);

  return (
    <div
      style={{ height }}
      className="overflow-hidden rounded-sm relative"
      data-testid="property-map"
    >
      <MapContainer
        center={coords}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
        zoomControl={false}
      >
        <Recenter coords={coords} zoom={zoom} />
        {view === "satellite" ? (
          <TileLayer
            attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={20}
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <Marker position={coords}>
          <Popup>{address}</Popup>
        </Marker>
      </MapContainer>

      {/* View toggle — top-right corner */}
      {!hideToggle && (
      <div
        className="absolute top-2 right-2 z-[500] flex items-center gap-1 bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full p-0.5 shadow-sm"
        data-testid="property-map-view-toggle"
      >
        <button
          onClick={() => setView("satellite")}
          data-testid="property-map-satellite-btn"
          className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
            view === "satellite"
              ? "bg-slate-900 text-white"
              : "text-slate-700 hover:bg-slate-100"
          }`}
          title="Aerial photo of the property"
        >
          Satellite
        </button>
        <button
          onClick={() => setView("map")}
          data-testid="property-map-map-btn"
          className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
            view === "map"
              ? "bg-slate-900 text-white"
              : "text-slate-700 hover:bg-slate-100"
          }`}
          title="Street map view"
        >
          Map
        </button>
      </div>
      )}

      {/* Street View link — bottom-left, opens Google Street View */}
      {!hideStreetViewLink && (
      <a
        href={streetViewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="property-map-streetview-link"
        className="absolute bottom-2 left-2 z-[500] inline-flex items-center gap-1 bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full px-3 py-1 shadow-sm text-[10px] uppercase tracking-widest text-slate-700 hover:bg-slate-100 transition-colors"
        title="Open Street View in a new tab"
      >
        Street View
        <ExternalLink size={10} strokeWidth={2.2} className="opacity-70" />
      </a>
      )}
    </div>
  );
}
